import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FormikContextType, useFormikContext } from 'formik'
import {
  Button,
  Column,
  Columns,
  IconAdd,
  TextField,
  Stack,
  Box,
  Text,
  PopoverRenderer,
  List,
  TextLink
} from '@machobear/design-system'
import { isNullOrEmpty } from '@/utils'
import { getLinkServiceDisplayName } from './util'
import LinkedVideoPreview from './linked-video-preview'
import { FormBox, FormBoxSkeleton, Counter } from '../components'
import InfoBox from '../components/info-box'
import { MediaFormValues } from '../types'

const TourSection = ({ isLoading }: { isLoading: boolean }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values }: FormikContextType<MediaFormValues> = useFormikContext<MediaFormValues>()

  const [url, setUrl] = useState('')

  const listingId = values.listing_id
  const listingLinks = values.listingLinks || {}
  const tour3d = listingLinks.tour_3d || {}
  const has3dTour = !isNullOrEmpty(tour3d)
  const displayName = getLinkServiceDisplayName(tour3d.link_service)

  const add3DTour = async () => {
    try {
      const response = await dispatch.listing.addVideoLink({
        listingId,
        linkUrl: url.replace(/\/shorts\//, '/embed/'),
        videoType: '3d'
      })
      if (!response.ok) {
        throw Error('Failed to add video link')
      }
    } catch {
      // error
      return
    }
    setUrl('')
  }

  return (
    <FormBox
      label={t('3D Tour Link', '3D Tour Link')}
      icon={
        <Counter
          name={t('3D Tour Link', '3D Tour Link')}
          count={has3dTour ? 1 : 0}
          max={1}
          isLoading={isLoading}
        />
      }
    >
      {isLoading ? (
        <FormBoxSkeleton />
      ) : (
        <>
          {has3dTour ? (
            <LinkedVideoPreview
              label={`${displayName} 3D Tour`}
              listingId={listingId}
              videoData={tour3d}
              videoType="3d"
              externalLink
            />
          ) : (
            <Stack space="small">
              <Columns space="gutter" collapseBelow="tablet">
                <Column width="2/3">
                  <TextField
                    id="tour_3d_url"
                    label={t('3D Tour URL', '3D Tour URL')}
                    placeholder=""
                    value={url}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                  />
                </Column>
                <Column>
                  <Button
                    icon={<IconAdd />}
                    variant="ghost"
                    size="large"
                    onClick={add3DTour}
                    disabled={url?.length < 1}
                  >
                    {t('Add 3D Tour')}
                  </Button>
                </Column>
              </Columns>
              <PopoverRenderer
                placement="top"
                trigger={({ triggerProps }) => (
                  <Box
                    aria-label="3D Tour tooltip"
                    style={{ width: 'fit-content' }}
                    {...triggerProps}
                  >
                    <InfoBox>
                      <u>
                        {t('View list of supported 3D Tours', 'View list of supported 3D Tours')}
                      </u>
                    </InfoBox>
                  </Box>
                )}
              >
                <Box paddingBottom="xxsmall">
                  <Stack space="medium">
                    <Text weight="strong">{t('3D tours supported:', '3D tours supported:')}</Text>
                    <List space="xxsmall">
                      <Text size="small">Eyexpo</Text>
                      <Text size="small">YouTube</Text>
                      <Text size="small">Matterport</Text>
                      <Text size="small">Metareal</Text>
                      <Text size="small">iGUIDE</Text>
                    </List>
                    <Text size="small">
                      {t('Don’t see yours listed?', 'Don’t see yours listed?')}
                    </Text>
                    <Text tone="neutral" weight="strong">
                      <TextLink href="mailto:support@liv.rent" target="_blank">
                        {t('common.contact_us', 'Contact us')}
                      </TextLink>
                    </Text>
                  </Stack>
                </Box>
              </PopoverRenderer>
            </Stack>
          )}
        </>
      )}
    </FormBox>
  )
}

export default TourSection
